import type { ComponentType, SVGProps } from "react";
import { Mail, MessageSquare } from "lucide-react";
import { TelegramIcon } from "@/components/icons/TelegramIcon";
import { WhatsAppIcon } from "@/components/icons/WhatsAppIcon";

export interface ChannelRow {
  provider: string;
  contact: Record<string, unknown>;
  opted_in: boolean;
  revoked_at: string | null;
}

export interface ChannelManifestEntry {
  provider: string;
  /** i18n key under profile.channels.names.* — never a literal label. */
  labelKey: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  iconColor: string;
  /**
   * Whether this channel can appear as its own row in the Channels UI.
   * False only for whatsapp_meta, which shares the WhatsApp label — two rows
   * would confuse. email and sms became visible in DEV-787 once each had a way
   * back: email via reconnect_email_contact(), sms via the OTP link flow.
   */
  visible: boolean;
  /**
   * Whether a user may start a NEW link for this channel.
   *
   * Split from `visible` deliberately: a retired channel must stop accepting
   * links while STAYING visible to whoever already linked it, or their
   * contact becomes invisible and they can no longer disconnect it — a phone
   * number they can't revoke. A row renders when
   * `visible && (connectable || connected)`.
   */
  connectable: boolean;
  /**
   * May a user UNLINK this channel? Mirrors
   * notification_channel_kinds.user_disconnectable, which is where the rule is
   * actually enforced — disconnect_channel() returns 'not_disconnectable' for
   * a FALSE row, so hiding the link here is presentation, not the guarantee.
   *
   * This is about unlinking, not about receiving. Every live channel,
   * email included, has a switch that pauses delivery through
   * user_contacts.opted_in (useSetChannelOptIn). Email is FALSE because its
   * address is the profile's, restored by reconnect_email_contact(). There is
   * nothing to unlink, but its switch still turns it off.
   */
  userDisconnectable: boolean;
  displayName: (contact: Record<string, unknown>) => string | null;
}

/** Mask a phone for display: "+595 •••• 4567". */
function maskPhone(phone: string): string {
  const cleaned = phone.replace(/[^\d+]/g, "");
  if (cleaned.length < 8) return cleaned;
  const last4 = cleaned.slice(-4);
  const head = cleaned.slice(0, cleaned.startsWith("+") ? 4 : 3);
  return `${head} •••• ${last4}`;
}

function phoneDisplay(contact: Record<string, unknown>): string | null {
  for (const key of ["phone", "phone_e164"]) {
    const v = contact[key];
    if (typeof v === "string" && v) return maskPhone(v);
  }
  return null;
}

export const CHANNELS = [
  {
    provider: "telegram",
    userDisconnectable: true,
    labelKey: "profile.channels.names.telegram",
    icon: TelegramIcon,
    iconColor: "text-[#26A5E4]",
    visible: true,
    connectable: true,
    displayName: (c: Record<string, unknown>) => {
      if (typeof c.username === "string" && c.username) return `@${c.username}`;
      if (typeof c.first_name === "string" && c.first_name) return c.first_name;
      return null;
    },
  },
  {
    // RETIRED (20260905140000): routes are disabled, so this channel no
    // longer delivers. Still `visible` so a user who already linked it can
    // see and disconnect their number; `connectable: false` stops anyone
    // new from linking a channel that would silently deliver nothing.
    provider: "whatsapp_openwa",
    userDisconnectable: true,
    labelKey: "profile.channels.names.whatsapp",
    icon: WhatsAppIcon,
    iconColor: "text-[#25D366]",
    visible: true,
    connectable: false,
    displayName: phoneDisplay,
  },
  {
    // Same user-visible channel as whatsapp_openwa; which transport carries a
    // message is an operator concern expressed in notification_routes priority.
    // Not visible, so users never see two WhatsApp rows.
    provider: "whatsapp_meta",
    userDisconnectable: true,
    labelKey: "profile.channels.names.whatsapp",
    icon: WhatsAppIcon,
    iconColor: "text-[#25D366]",
    visible: false,
    connectable: false,
    displayName: phoneDisplay,
  },
  {
    // Auto-connected for anyone who signed up with an email or Google
    // (sync_email_contact reads profiles.email). `connectable` is what makes
    // Disconnect safe to offer: the row would otherwise vanish on disconnect —
    // the render rule is `visible && (connectable || connected)` — leaving a
    // one-way opt-out. Connect here calls reconnect_email_contact(), which
    // restores the address of record from profiles.email without an OTP (that
    // column is self-asserted, not verified — see the RPC's header).
    provider: "email",
    // Not unlinkable (20260906120000); pausable via its switch like the rest.
    userDisconnectable: false,
    labelKey: "profile.channels.names.email",
    icon: Mail,
    iconColor: "text-text-secondary",
    visible: true,
    connectable: true,
    displayName: (c: Record<string, unknown>) =>
      typeof c.address === "string" ? c.address : null,
  },
  {
    // DEV-787 gave SMS an OTP link flow (sms-link-start / sms-link-verify) and
    // enabled its routes for the four Tier A events. It sits LAST in route
    // priority, so for a user who also has Telegram or email it costs nothing
    // and effectively never fires — it is the only channel a phone-only user
    // has.
    provider: "sms",
    userDisconnectable: true,
    labelKey: "profile.channels.names.sms",
    icon: MessageSquare,
    iconColor: "text-text-secondary",
    visible: true,
    connectable: true,
    displayName: phoneDisplay,
  },
] as const satisfies readonly ChannelManifestEntry[];

/** Keys of notification_channel_kinds. Derived, never hand-maintained. */
export type ChannelProvider = (typeof CHANNELS)[number]["provider"];

const BY_PROVIDER = new Map(CHANNELS.map((c) => [c.provider as string, c]));

/** Human-readable label for a linked contact, or null. */
export function getChannelDisplayName(row: ChannelRow): string | null {
  return BY_PROVIDER.get(row.provider)?.displayName(row.contact) ?? null;
}

export interface ActiveChannel {
  provider: string;
  labelKey: string;
  icon: ChannelManifestEntry["icon"];
  iconColor: string;
  displayName: string | null;
}

/**
 * The channels a member is actually reached on right now — linked and not
 * paused — in manifest order, one per label (whatsapp_openwa and whatsapp_meta
 * are both "WhatsApp" and show once). Unknown providers are skipped rather
 * than drawn without an icon.
 */
export function getActiveChannels(rows: readonly ChannelRow[]): ActiveChannel[] {
  const seen = new Set<string>();
  const out: ActiveChannel[] = [];
  for (const entry of CHANNELS) {
    if (seen.has(entry.labelKey)) continue;
    const row = rows.find((r) => r.provider === entry.provider && !r.revoked_at && r.opted_in);
    if (!row) continue;
    seen.add(entry.labelKey);
    out.push({
      provider: entry.provider,
      labelKey: entry.labelKey,
      icon: entry.icon,
      iconColor: entry.iconColor,
      displayName: entry.displayName(row.contact),
    });
  }
  return out;
}
